import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { useGlobalState } from '../../contexts/GlobalStateContext';


const AlertBanner = styled.div`
  background: #fffbe6;
  color: #856404;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 18px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(186,130,79,0.05);
  font-size: 1rem;
  z-index: 100;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
    padding: 16px 10px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BannerActions = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 600px) {
    width: 100%;
    flex-direction: column;
    gap: 8px;
  }
`;

const BannerButton = styled.button`
  background: #ba824f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #a77445;
  }
`;
 

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 20px 12px 32px;
  }
`;

const WelcomeHeader = styled.div`
  background: white;
  padding: 20px 40px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h1 {
    font-size: 1.5rem;
    color: #333;
    font-weight: 500;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h2 {
    font-size: 1rem;
    color: #666;
    margin-bottom: 16px;
  }
`;

const PlanInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  .plan-amount {
    font-size: 1.25rem;
    font-weight: 500;
    color: #333;
  }

  .plan-status {
    background: #e6f4ea;
    color: #1e7e34;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.875rem;
  }

  .plan-period {
    color: #666;
    font-size: 0.875rem;
  }
`;

const PlanExpiry = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  span {
    color: #666;
    font-size: 0.875rem;
  }
`;

const ChangePlanButton = styled.button`
  background: #ba824f;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.3s ease;

  &:hover {
    background: #a77445;
  }
`;

const VisitsCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h2 {
    font-size: 1rem;
    color: #666;
    margin-bottom: 16px;
  }

  .period {
    text-align: right;
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 16px;
  }
`;

const VisitsStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  text-align: center;

  .stat {
    h3 {
      font-size: 2rem;
      color: #333;
      margin-bottom: 8px;
    }
    p {
      color: #666;
      font-size: 0.875rem;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PropertyCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h2 {
    font-size: 1rem;
    color: #666;
    margin-bottom: 16px;
  }

  .period {
    text-align: right;
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 16px;
  }

  .count {
    font-size: 2rem;
    color: #333;
    text-align: center;
  }
`;

const MessagesSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;

  h2 {
    font-size: 1rem;
    color: #666;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .period {
      color: #666;
      font-size: 0.875rem;
    }
  }
`;

const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  .user {
    display: flex;
    align-items: center;
    gap: 12px;

    .avatar {
      width: 32px;
      height: 32px;
      background: #eee;
      border-radius: 50%;
    }

    .name {
      color: #333;
      font-size: 0.875rem;
    }
  }

  .reply {
    color: #ba824f;
    font-size: 0.875rem;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ReservationsSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h2 {
    font-size: 1rem;
    color: #666;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    a {
      color: #ba824f;
      font-size: 0.875rem;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ReservationsTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    font-size: 0.875rem;
  }

  th {
    color: #666;
    font-weight: 500;
    border-bottom: 1px solid #eee;
  }

  td {
    color: #333;
  }

  .status {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    
    &.available {
      background: #e6f4ea;
      color: #1e7e34;
    }
  }

  .action-button {
    background: #34574A;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;

    &:hover {
      background: #2a4539;
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Dashboard = () => {
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem('terms_alert_accepted');
    if (!accepted) setShowAlert(true);
  }, []);

  const handleAccept = () => {
    window.open('/privacy-policy', '_blank');
  };
  const handleClose = () => {
    setShowAlert(false);
    localStorage.setItem('terms_alert_accepted', '1');
  };

  const [toast, setToast] = React.useState(null);
  const navigate = useNavigate();
  const { user } = useGlobalState();
  const API_URL = import.meta.env.VITE_URL_API;

  // Dashboard data states
  const [dashLoading, setDashLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [kpis, setKpis] = useState({
    adsUsed: 0,
    adsTotal: 0,
    highlightsUsed: 0,
    highlightsTotal: 0,
    listingsTotal: 0,
    favorites: 0,
    reservationsActive: 0,
  });
  const [chartData, setChartData] = useState({
    usageBar: [],
    listingsLine: [],
  });
  const [usagePage, setUsagePage] = useState(1);
  const USAGE_PAGE_SIZE = 6;


  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  }

  function handleChangePlan() {
    navigate('/dashboard/packages');
  }
  function handlePayNow() {
    showToast('Ação de teste: Pagar reserva!');
  }
  function handleReplyMessage() {
    showToast('Ação de teste: Responder mensagem!');
  }

  // Helpers
  const safeFetch = async (url, token) => {
    try {
      const resp = await fetch(url, {
        headers: { 
          accept: 'application/json', 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch {
      return null;
    }
  };

  const computePlansFromCheckouts = (checkouts, listingsMe) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    // Contar anúncios reais por pacote
    const listingsArray = listingsMe?.imoveis || [];
    const contagemPorPacote = {};
    listingsArray.forEach(anuncio => {
      if (anuncio.pacoteIMOVEL) {
        const pacoteKey = String(anuncio.pacoteIMOVEL);
        contagemPorPacote[pacoteKey] = (contagemPorPacote[pacoteKey] || 0) + 1;
      }
    });
    
    return (checkouts || []).map(item => {
      const start = new Date(item.dataCHECKOUT);
      const end = new Date(start);
      const days = Number(item.periodoCHECKOUT) || 30;
      end.setDate(end.getDate() + days);
      end.setHours(23, 59, 59, 999);
      let status = 'Expirado';
      if (item.ativoCHECKOUT === 5) status = 'Cancelado';
      else if (end > now) status = 'Ativo';
      const planType = item.plano?.tipoPLANO;
      const isHighlightPlan = planType === 5;
      
      // Usar contagem real de anúncios do frontend
      const pacoteId = String(item.codigoCHECKOUT);
      const usadosReal = contagemPorPacote[pacoteId] || 0;
      
      return {
        id: item.codigoCHECKOUT,
        title: item.nomeCHECKOUT || item.plano?.nomePLANO || 'Pacote',
        status,
        startDate: start.toLocaleDateString('pt-BR'),
        endDate: end.toLocaleDateString('pt-BR'),
        adsUsed: usadosReal,
        adsTotal: Number(item.plano?.quantidadePLANO || item.quantidadeCHECKOUT || 0),
        highlightsUsed: Number(item.destaquesUsados || 0),
        highlightsTotal: Number(item.quantidadeCHECKOUT || 0),
        isHighlightPlan,
        raw: item,
      };
    });
  };

  useEffect(() => {
    const load = async () => {
      setDashLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        
        // Extrai userId do token
        let userId = '';
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.codigo || payload.id || payload.userId || '';
        } catch (e) {
          console.error('[Dashboard] Erro ao decodificar token:', e);
        }
        
        const [checkouts, listingsMe, favorites, reservations] = await Promise.all([
          safeFetch(`${API_URL}/checkouts`, token),
          safeFetch(`${API_URL}/imoveis/me`, token),
          userId ? safeFetch(`${API_URL}/favoritos?usuario=${userId}`, token) : safeFetch(`${API_URL}/favoritos`, token),
          safeFetch(`${API_URL}/reservas/me?page=1&pageSize=100`, token), // Usa /me para pegar apenas do usuário logado
        ]);

        const plans = computePlansFromCheckouts((checkouts?.checkouts || checkouts || []), listingsMe);
        const pick = [...plans].sort((a,b) => new Date(b.raw?.dataCHECKOUT) - new Date(a.raw?.dataCHECKOUT))[0] || null;
        setCurrentPlan(pick);

        const adsTotal = plans.reduce((s,p) => s + (p.isHighlightPlan ? 0 : p.adsTotal), 0);
        const highlightsUsed = plans.reduce((s,p) => s + (p.isHighlightPlan ? p.highlightsUsed : 0), 0);
        const highlightsTotal = plans.reduce((s,p) => s + (p.isHighlightPlan ? p.highlightsTotal : 0), 0);

        // Usar dados do /imoveis/me
        console.log('[Dashboard] listingsMe raw:', listingsMe);
        const listingsArray = listingsMe?.imoveis || [];
        console.log('[Dashboard] listingsArray:', listingsArray);
        console.log('[Dashboard] Total de anúncios retornados:', listingsArray.length);
        
        // Verificar anúncios ativos vs inativos
        const activeListings = listingsArray.filter(listing => listing.ativoIMOVEL === 1);
        const inactiveListings = listingsArray.filter(listing => listing.ativoIMOVEL !== 1);
        console.log('[Dashboard] Anúncios ATIVOS:', activeListings.length);
        console.log('[Dashboard] Anúncios INATIVOS:', inactiveListings.length);
        console.log('[Dashboard] Anúncios ativos:', activeListings.map(l => ({ id: l.codigoIMOVEL, titulo: l.tituloIMOVEL, ativo: l.ativoIMOVEL })));
        
        // Usa o countAnuncios da API se disponível, senão usa o length do array
        const apiCount = listingsMe?.limitesPlano?.countAnuncios;
        const arrayCount = listingsArray.length;
        const listingsTotal = apiCount || arrayCount;
        
        console.log('[Dashboard] Contagem de anúncios:', {
          countAnunciosAPI: apiCount,
          lengthArray: arrayCount,
          finalCount: listingsTotal,
          diferenca: apiCount && arrayCount ? apiCount - arrayCount : 0
        });
        
        // Debug favoritos
        console.log('[Dashboard] Favoritos raw:', favorites);
        console.log('[Dashboard] userId:', userId);
        let favoritesArray = [];
        if (Array.isArray(favorites)) {
          favoritesArray = favorites;
        } else if (favorites?.items) {
          favoritesArray = favorites.items;
        } else if (favorites?.data) {
          favoritesArray = favorites.data;
        } else if (favorites?.favoritos) {
          favoritesArray = favorites.favoritos;
        }
        
        // Filtra favoritos do usuário logado (caso a API não filtre)
        if (userId) {
          favoritesArray = favoritesArray.filter(fav => 
            String(fav.clienteFAVORITO || fav.usuarioFAVORITO || fav.userId) === String(userId)
          );
        }
        
        const favoritesCount = favoritesArray.length;
        console.log('[Dashboard] Favoritos filtrados count:', favoritesCount, favoritesArray);
        
        // Debug reservas
        console.log('[Dashboard] Reservas raw:', reservations);
        console.log('[Dashboard] Reservas type:', typeof reservations, Array.isArray(reservations));
        
        // A API /reservas/me pode retornar { items: [...] } ou array direto
        let reservationsArray = [];
        if (Array.isArray(reservations)) {
          reservationsArray = reservations;
        } else if (reservations?.items) {
          reservationsArray = reservations.items;
        } else if (reservations?.data) {
          reservationsArray = reservations.data;
        }
        
        console.log('[Dashboard] Reservas do usuário:', reservationsArray.length, reservationsArray);
        
        // Conta reservas ativas (não canceladas)
        console.log('[Dashboard] Verificando status das reservas:');
        reservationsArray.forEach(r => {
          console.log(`  Reserva ${r.codigoRESERVA}:`, {
            statusRESERVA: r.statusRESERVA,
            tipo: typeof r.statusRESERVA,
            ativoRESERVA: r.ativoRESERVA,
            statuspagamentoRESERVA: r.statuspagamentoRESERVA,
          });
        });
        
        // statusRESERVA: 1 = Em Andamento, 2 = Concluída, 3 = Cancelada, 4 = Pendente
        // Conta todas exceto canceladas (3)
        const reservationsActive = reservationsArray.filter(r => {
          const isActive = r.statusRESERVA !== 3 && r.ativoRESERVA === 1;
          return isActive;
        }).length;
        console.log('[Dashboard] Reservas ATIVAS (não canceladas) count:', reservationsActive, 'de', reservationsArray.length);

        // adsUsed deve refletir os anúncios efetivamente cadastrados; usa ativo ou total retornado
        const adsUsedFromListings = activeListings.length > 0 ? activeListings.length : listingsArray.length;

        setKpis({
          adsUsed: adsUsedFromListings,
          adsTotal,
          highlightsUsed,
          highlightsTotal,
          listingsTotal,
          favorites: favoritesCount,
          reservationsActive,
        });

        // Dados para gráficos
        // Gráfico de barras: uso por plano ativo
        const activePlans = plans.filter(p => p.status === 'Ativo');
        const usageBarData = activePlans.map(p => ({
          label: p.title.substring(0, 15) + (p.title.length > 15 ? '...' : ''),
          value: p.isHighlightPlan ? p.highlightsUsed : p.adsUsed,
        }));

        // Gráfico de linha: imóveis por mês (últimos 6 meses)
        const now = new Date();
        const monthsData = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthName = d.toLocaleDateString('pt-BR', { month: 'short' });
          const count = listingsArray.filter(item => {
            const createdDate = new Date(item.dataCadastroIMOVEL || item.dataIMOVEL || item.createdAt);
            return createdDate.getMonth() === d.getMonth() && createdDate.getFullYear() === d.getFullYear();
          }).length;
          monthsData.push({ label: monthName, value: count });
        }

        setChartData({
          usageBar: usageBarData,
          listingsLine: monthsData,
        });
        // sempre volta pra primeira página quando recarregar dados
        setUsagePage(1);
      } catch (e) {
        console.error('Não foi possível carregar dados do dashboard:', e);
      } finally {
        setDashLoading(false);
      }
    };
    load();
  }, [API_URL]);

  const UsageChart = ({ used = 0, total = 0, label }) => {
    const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: '.85rem', color: '#666' }}>{label}: {used}/{total}</div>
        <div style={{ height: 10, background: '#eee', borderRadius: 999 }}>
          <div style={{ width: pct + '%', height: '100%', background: '#ba824f', borderRadius: 999 }} />
        </div>
      </div>
    );
  };

  // Gráfico de barras simples
  const BarChart = ({ data, title }) => {
    if (!data || data.length === 0) return null;

    // valor máximo para normalizar altura em PX
    const maxValue = Math.max(...data.map(d => d.value), 1);

    const formatLabel = (label) => {
      const str = String(label || '').trim();
      return str.length <= 20 ? str : str.slice(0, 19) + '…';
    };

    return (
      <div style={{ background: 'white', padding: 24, borderRadius: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <h2 style={{ fontSize: '1rem', color: '#666', marginBottom: 16 }}>{title}</h2>

        <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 16,
              minHeight: 200,
              minWidth: data.length > 6 ? data.length * 60 : '100%',
            }}
          >
            {data.map((item, idx) => {
              const h = (item.value / maxValue) * 180; // altura em px
              return (
                <div
                  key={idx}
                  style={{
                    flex: '0 0 56px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{item.value}</div>

                  <div
                    style={{
                      width: '100%',
                      height: Math.max(h, item.value > 0 ? 8 : 2),
                      background: '#ba824f',
                      borderRadius: '4px 4px 0 0',
                    }}
                  />

                  <div
                    title={String(item.label || '')}
                    style={{
                      fontSize: 12,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%',
                    }}
                  >
                    {formatLabel(item.label)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Paginação do gráfico de uso por plano
  const totalUsagePages = Math.max(1, Math.ceil(chartData.usageBar.length / USAGE_PAGE_SIZE));
  const usageDataPage = chartData.usageBar.slice(
    (usagePage - 1) * USAGE_PAGE_SIZE,
    usagePage * USAGE_PAGE_SIZE,
  );

  // Gráfico de linha simples
  const LineChart = ({ data, title }) => {
    if (!data || data.length === 0) return null;
    const maxValue = Math.max(...data.map(d => d.value), 1);
    const width = 600;
    const height = 200;
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;
    
    const points = data.map((item, idx) => {
      const x = padding + (idx / (data.length - 1 || 1)) * chartWidth;
      const y = padding + chartHeight - (item.value / maxValue) * chartHeight;
      return { x, y, value: item.value, label: item.label };
    });

    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
      <div style={{ background: 'white', padding: 24, borderRadius: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <h2 style={{ fontSize: '1rem', color: '#666', marginBottom: 16 }}>{title}</h2>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
            const y = padding + chartHeight * (1 - pct);
            return (
              <g key={i}>
                <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#eee" strokeWidth="1" />
                <text x={padding - 5} y={y + 4} textAnchor="end" fontSize="10" fill="#999">
                  {Math.round(maxValue * pct)}
                </text>
              </g>
            );
          })}
          
          {/* Line */}
          <path d={pathD} fill="none" stroke="#ba824f" strokeWidth="3" />
          
          {/* Points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4" fill="#ba824f" />
              <text x={p.x} y={height - padding + 15} textAnchor="middle" fontSize="10" fill="#666">
                {p.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  return (
    <PageContainer>
      {showAlert && (
        <AlertBanner>
          <span>
            <b>Aviso:</b> Os Termos e Condições foram atualizados. Por favor, leia os novos termos.
          </span>
          <BannerActions>
            <BannerButton onClick={handleAccept}>Ler termos</BannerButton>
            <BannerButton style={{background:'#ccc',color:'#333'}} onClick={handleClose}>Fechar</BannerButton>
          </BannerActions>
        </AlertBanner>
      )}
      {toast && (
        <div style={{position:'fixed',top:24,right:24,zIndex:9999,background:toast.type==='error'?'#e74c3c':'#27ae60',color:'#fff',padding:'16px 32px',borderRadius:8,fontWeight:600,boxShadow:'0 2px 16px rgba(0,0,0,0.08)'}}>
          {toast.msg}
        </div>
      )}

      <StatsGrid>
        {/* Meu Plano (real) */}
        <PlanCard>
          <h2>Meu Plano</h2>
          {dashLoading ? (
            <div style={{color:'#888'}}>Carregando...</div>
          ) : currentPlan ? (
            <>
              <PlanInfo>
                <span className="plan-amount">{currentPlan.title}</span>
                <span className="plan-status" style={{
                  background: currentPlan.status === 'Ativo' ? '#e6f4ea' : (currentPlan.status === 'Cancelado' ? '#f5f5f5' : '#ffebee'),
                  color: currentPlan.status === 'Ativo' ? '#1e7e34' : (currentPlan.status === 'Cancelado' ? '#757575' : '#c62828')
                }}>{currentPlan.status}</span>
                <span className="plan-period">{currentPlan.startDate} - {currentPlan.endDate}</span>
              </PlanInfo>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 16 }}>
                <UsageChart label="Anúncios" used={kpis.adsUsed} total={kpis.adsTotal} />
                <UsageChart label="Destaques" used={kpis.highlightsUsed} total={kpis.highlightsTotal} />
              </div>
              <PlanExpiry>
                <span>Validade: {currentPlan.endDate}</span>
                <ChangePlanButton onClick={e => { e.stopPropagation(); handleChangePlan(); }}>Contratar/Alterar</ChangePlanButton>
              </PlanExpiry>
            </>
          ) : (
            <>
              <div style={{color:'#888',margin:'24px 0'}}>Nenhum plano ativo</div>
              <ChangePlanButton onClick={() => navigate('/dashboard/packages')}>Contratar Plano</ChangePlanButton>
            </>
          )}
        </PlanCard>

        {/* KPIs rápidos */}
        <VisitsCard>
          <h2>Resumo</h2>
          <div className="period">Atual</div>
          <VisitsStats>
            <div className="stat">
              <h3><CountUp end={kpis.listingsTotal} duration={1.2} /></h3>
              <p>Imóveis</p>
            </div>
            <div className="stat">
              <h3><CountUp end={kpis.favorites} duration={1.2} /></h3>
              <p>Favoritos</p>
            </div>
          </VisitsStats>
        </VisitsCard>
      </StatsGrid>

      <PropertiesGrid>
        <PropertyCard>
          <h2>Anúncios usados</h2>
          <div className="period">Capacidade</div>
          <div className="count">{kpis.adsTotal > 0 ? `${kpis.adsUsed}/${kpis.adsTotal}` : '—'}</div>
        </PropertyCard>
        <PropertyCard>
          <h2>Destaques usados</h2>
          <div className="period">Capacidade</div>
          <div className="count">{kpis.highlightsTotal > 0 ? `${kpis.highlightsUsed}/${kpis.highlightsTotal}` : '—'}</div>
        </PropertyCard>
        <PropertyCard>
          <h2>Minhas Reservas</h2>
          <div className="period">Ativas</div>
          <div className="count"><CountUp end={kpis.reservationsActive} duration={1.2} /></div>
        </PropertyCard>
      </PropertiesGrid>

      {/* Gráficos */}
      <ChartsGrid>
        {usageDataPage.length > 0 && (
          <div>
            <BarChart data={usageDataPage} title="Uso de Anúncios por Plano" />
            {totalUsagePages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setUsagePage(p => Math.max(1, p - 1))}
                  disabled={usagePage === 1}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: '1px solid #ddd',
                    background: usagePage === 1 ? '#f5f5f5' : 'white',
                    cursor: usagePage === 1 ? 'default' : 'pointer',
                  }}
                >
                  ‹
                </button>
                <span style={{ fontSize: 12, color: '#555' }}>
                  Página {usagePage} de {totalUsagePages}
                </span>
                <button
                  type="button"
                  onClick={() => setUsagePage(p => Math.min(totalUsagePages, p + 1))}
                  disabled={usagePage === totalUsagePages}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: '1px solid #ddd',
                    background: usagePage === totalUsagePages ? '#f5f5f5' : 'white',
                    cursor: usagePage === totalUsagePages ? 'default' : 'pointer',
                  }}
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}
        {chartData.listingsLine.length > 0 && (
          <LineChart data={chartData.listingsLine} title="Imóveis Cadastrados (Últimos 6 Meses)" />
        )}
      </ChartsGrid>

        {/* Mensagens WhatsApp: mostrar só se houver mensagens */}
        {user?.messages?.length > 0 ? (
          <MessagesSection onClick={() => navigate('/dashboard/messages')} style={{cursor:'pointer'}}>
            <h2>
              Mensagens WhatsApp
              <span className="period">Últimos 30 dias</span>
            </h2>
            <MessagesList>
              {user.messages.slice(0,3).map((msg, idx) => (
                <MessageItem key={idx}>
                  <div className="user">
                    <div className="avatar" />
                    <span className="name">{msg.name}</span>
                  </div>
                  <span className="reply" onClick={e => { e.stopPropagation(); handleReplyMessage(); }}>Responder</span>
                </MessageItem>
              ))}
            </MessagesList>
          </MessagesSection>
        ) : null}

      {/* Reservas: mostrar só se houver reservas */}
      {user?.reservations?.length > 0 ? (
        <ReservationsSection>
          <h2>
            Minhas Reservas
            <a href="#" onClick={e => {e.preventDefault(); navigate('/dashboard/reservations')}}>Ver todas ({user.reservations.length})</a>
          </h2>
          <ReservationsTable>
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Data</th>
                <th>Endereço</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Hóspedes</th>
                <th>Pets</th>
                <th>Subtotal</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {user.reservations.slice(0,1).map((res, idx) => (
                <tr key={idx} onClick={() => navigate('/dashboard/reservations')} style={{cursor:'pointer'}}>
                  <td>{res.id}</td>
                  <td><span className="status available">{res.status}</span></td>
                  <td>{res.date}</td>
                  <td>{res.address}</td>
                  <td>{res.checkin}</td>
                  <td>{res.checkout}</td>
                  <td>{res.guests}</td>
                  <td>{res.pets ? 'Sim' : 'Não'}</td>
                  <td>{res.subtotal}</td>
                  <td><button className="action-button" onClick={e => { e.stopPropagation(); handlePayNow(); }}>Pagar Agora</button></td>
                </tr>
              ))}
            </tbody>
          </ReservationsTable>
        </ReservationsSection>
      ) : null}
    </PageContainer>
  );
};

export default Dashboard;
